/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useEffect, useState } from 'react';
import { X, Car, Wrench, Sofa, Dog, Leaf, Camera, Laptop, Watch, Heart, Tag } from 'lucide-react';
import { useTheme } from '../theme';
import { itemService } from '../services/itemService';

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (category: string) => void;
}

interface ApiCategory {
  id: number;
  name: string;
}

interface DisplayCategory {
  name: string;
  icon: React.ReactNode;
  color: string;
}

// Visual presentation defaults keyed by category name (case-insensitive).
const CATEGORY_PRESENTATION: Record<string, { icon: React.ReactNode; color: string }> = {
  vehicles:    { icon: <Car size={24} />,    color: '#3b82f6' },
  tools:       { icon: <Wrench size={24} />, color: '#6b7280' },
  furniture:   { icon: <Sofa size={24} />,   color: '#f59e0b' },
  pets:        { icon: <Dog size={24} />,    color: '#ef4444' },
  garden:      { icon: <Leaf size={24} />,   color: '#10b981' },
  electronics: { icon: <Laptop size={24} />, color: '#6366f1' },
  hobbies:     { icon: <Camera size={24} />, color: '#ec4899' },
  fashion:     { icon: <Watch size={24} />,  color: '#8b5cf6' },
  gifts:       { icon: <Heart size={24} />,  color: '#f43f5e' },
};

const FALLBACK_PRESENTATION = { icon: <Tag size={24} />, color: '#64748b' };

const CategoryModal: React.FC<CategoryModalProps> = ({ isOpen, onClose, onSelect }) => {
  const theme = useTheme();
  const [categories, setCategories] = useState<DisplayCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const fetchCategories = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await itemService.getCategories();
        const list: ApiCategory[] = result?.data?.data ?? [];
        if (cancelled) return;
        const mapped: DisplayCategory[] = list.map((cat) => {
          const presentation =
            CATEGORY_PRESENTATION[cat.name.toLowerCase()] ?? FALLBACK_PRESENTATION;
          return { name: cat.name, ...presentation };
        });
        setCategories(mapped);
      } catch (err) {
        console.error('Failed to fetch categories', err);
        if (!cancelled) setError('Failed to load categories.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCategories();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="relative w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200"
        style={{ backgroundColor: theme.colors.surface }}
      >
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: theme.colors.border }}>
          <h2 className="text-xl font-bold" style={{ color: theme.colors.text }}>Browse Categories</h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            style={{ color: theme.colors.mutedText }}
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-8">
          {loading && (
            <p className="text-center text-sm font-medium" style={{ color: theme.colors.mutedText }}>
              Loading categories…
            </p>
          )}
          {error && !loading && (
            <p className="text-center text-sm font-medium text-red-500">{error}</p>
          )}
          {!loading && !error && categories.length === 0 && (
            <p className="text-center text-sm font-medium" style={{ color: theme.colors.mutedText }}>
              No categories available.
            </p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
            {categories.map((cat) => (
              <button
                key={cat.name}
                onClick={() => onSelect(cat.name)}
                className="group flex flex-col items-center gap-4 p-6 rounded-2xl border transition-all hover:shadow-lg hover:scale-105"
                style={{ 
                  backgroundColor: theme.colors.background, 
                  borderColor: theme.colors.border 
                }}
              >
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg transition-transform group-hover:rotate-6"
                  style={{ backgroundColor: cat.color }}
                >
                  {cat.icon}
                </div>
                <span className="font-bold" style={{ color: theme.colors.text }}>{cat.name}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 bg-gray-50 border-t" style={{ borderColor: theme.colors.border }}>
          <p className="text-center text-sm font-medium" style={{ color: theme.colors.mutedText }}>
            Select a category to see local listings
          </p>
        </div>
      </div>
    </div>
  );
};

export default CategoryModal;
